import React, { useEffect, useState } from 'react';
import { adminService } from '../../services/admin.service';
// Added missing import for Badge component
import Badge from '../../components/common/Badge';

const AdminBtcAddress: React.FC = () => {
  const [address, setAddress] = useState('');
  const [tempAddress, setTempAddress] = useState('');
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAddr = async () => {
    try {
      const res = await adminService.getBtcAddress();
      // Use the same robust extraction logic as the dashboard
      const addr = res?.data?.btcAddress || res?.data?.address || res?.btcAddress || res?.address || '';
      setAddress(addr);
      setTempAddress(addr);
    } catch (err: any) {
      console.error("Signal Sync Error:", err);
      setError("Failed to synchronize with broadcast node.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAddr();
  }, []);

  const handleUpdate = async () => {
    if (!tempAddress) return;
    setSaving(true);
    setError(null);
    try {
      const res = await adminService.updateBtcAddress(tempAddress);
      if (res.status) {
        setAddress(tempAddress);
        setIsEditing(false);
        alert(res.message || "BTC Broadcast Node Updated Successfully");
      } else {
        alert(res.message || "Transmission Protocol Error");
      }
    } catch (err: any) {
      alert(err.message || "Network link failure during broadcast");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setTempAddress(address);
    setIsEditing(false);
  };

  if (loading) return (
    <div className="h-96 flex items-center justify-center">
      <div className="w-12 h-12 border-4 border-purple-600 border-t-transparent animate-spin rounded-full"></div>
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto space-y-10 animate-in fade-in slide-in-from-bottom-6 duration-700">
      <div className="text-center px-4">
        <h2 className="text-4xl font-black text-white uppercase tracking-tighter">Broadcast Terminal</h2>
        <p className="text-slate-500 text-[10px] font-black uppercase tracking-[0.5em] mt-2">Global Deposit Protocol Configuration</p>
      </div>

      <div className="bg-[#030614] border border-white/5 rounded-[3.5rem] p-10 lg:p-16 shadow-2xl relative overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-purple-600/5 blur-[100px] pointer-events-none"></div>
        <div className="absolute -bottom-20 -left-20 w-80 h-80 bg-indigo-600/5 blur-[120px] pointer-events-none"></div>
        
        <div className="relative z-10">
          <div className="flex items-center justify-between mb-12">
            <div className="flex items-center space-x-4">
              <div className="w-14 h-14 bg-purple-600/10 rounded-2xl flex items-center justify-center border border-purple-500/20 shadow-inner">
                <svg className="w-7 h-7 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-black text-white uppercase tracking-tight">BTC Protocol Node</h3>
                <p className="text-[10px] text-slate-500 font-black uppercase tracking-widest mt-1">Status: {address ? 'Active & Broadcasting' : 'Node Disconnected'}</p>
              </div>
            </div>
            {address && !isEditing && (
               <Badge label="Mainnet Ready" variant="success" />
            )}
          </div>

          <div className="space-y-8">
            <div className="group relative">
              <label className="block text-[10px] font-black text-slate-600 uppercase tracking-[0.3em] mb-4 ml-4">Target Destination Address (BTC/USDT)</label>
              
              {isEditing ? (
                <div className="space-y-6 animate-in fade-in zoom-in-95 duration-300">
                  <input 
                    type="text" 
                    autoFocus
                    value={tempAddress}
                    onChange={e => setTempAddress(e.target.value)}
                    className="w-full bg-[#05071a] border border-purple-500/40 rounded-3xl px-8 py-6 text-white font-mono text-sm outline-none focus:ring-4 focus:ring-purple-600/10 transition-all shadow-inner placeholder-slate-900"
                    placeholder="Enter bc1q or network hash..."
                  />
                  <div className="flex items-center space-x-4">
                    <button 
                      onClick={handleCancel}
                      className="flex-1 py-5 bg-slate-900 text-slate-500 hover:text-white font-black text-[11px] uppercase rounded-2xl transition-all border border-white/5 active:scale-95"
                    >
                      Abort Modification
                    </button>
                    <button 
                      onClick={handleUpdate}
                      disabled={saving || !tempAddress}
                      className="flex-[2] py-5 bg-purple-600 text-white font-black text-[11px] uppercase rounded-2xl transition-all shadow-2xl shadow-purple-600/30 hover:bg-purple-500 disabled:opacity-30 active:scale-95"
                    >
                      {saving ? 'Synchronizing Node...' : 'Confirm Broadcast Update'}
                    </button>
                  </div>
                </div>
              ) : (
                <div 
                  onClick={() => setIsEditing(true)}
                  className="w-full bg-[#05071a]/50 border border-white/5 rounded-[2.5rem] px-10 py-8 flex items-center justify-between cursor-pointer group/node hover:border-purple-500/30 transition-all backdrop-blur-sm"
                >
                  <div className="flex flex-col">
                    <span className="text-white font-mono text-base tracking-widest break-all">
                      {address || '0x0000000000000000000000000000000000000000'}
                    </span>
                    {!address && <span className="text-rose-500 text-[9px] font-black uppercase mt-2 tracking-widest">Warning: Zero-address detected. Deposits disabled.</span>}
                  </div>
                  <div className="flex items-center space-x-4 text-slate-700 group-hover/node:text-purple-400 transition-colors shrink-0 ml-6">
                    <span className="text-[10px] font-black uppercase tracking-[0.2em] hidden sm:block">Modify Node</span>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                    </svg>
                  </div>
                </div>
              )}
            </div>

            {/* Network Intel Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-6">
              <div className="bg-[#05071a]/60 p-6 rounded-[2rem] border border-white/5">
                <p className="text-[9px] font-black text-slate-600 uppercase mb-3 tracking-widest">Protocol Type</p>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 rounded-full bg-purple-500"></div>
                  <p className="text-xs text-white font-black uppercase">BTC SegWit/Taproot</p>
                </div>
              </div>
              <div className="bg-[#05071a]/60 p-6 rounded-[2rem] border border-white/5">
                <p className="text-[9px] font-black text-slate-600 uppercase mb-3 tracking-widest">Global Sync</p>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                  <p className="text-xs text-white font-black uppercase">Propagated</p>
                </div>
              </div>
              <div className="bg-[#05071a]/60 p-6 rounded-[2rem] border border-white/5">
                <p className="text-[9px] font-black text-slate-600 uppercase mb-3 tracking-widest">Security Layer</p>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 rounded-full bg-indigo-500"></div>
                  <p className="text-xs text-white font-black uppercase">ECC Encrypted</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="mt-12 pt-8 border-t border-white/5">
            <p className="text-[10px] text-slate-600 font-bold uppercase tracking-widest leading-relaxed text-center max-w-xl mx-auto">
              Any changes to this node will be broadcasted across the entire trading network. Users will immediately see the updated address for all deposit operations.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminBtcAddress;